// The phone's Scan page (05 §5.2 Shell, Scan): look one box up by its label. Every read is one
// document or one scoped, limited query, allowed by the rules for the person who scans: a box where
// it was collected, where it is now or where it is going (office staff), or a box planned for the
// driver; its shipment the same way; and the box's scans recorded at the phone's branch. Firestore
// answers from its offline cache when there is no signal, and says whether it did.
import {
  collection,
  doc,
  limit,
  onSnapshot,
  query,
  where,
  type DocumentData,
  type DocumentReference,
  type Firestore,
  type FirestoreError,
} from 'firebase/firestore';
import { barcodeSchema, pieceIdSchema, trackingNoSchema } from '@ecobox/schema';
import type { BoxRow, ShipmentRow } from './counter';
import { toDoc, type Unsubscribe } from './docs';

/** What the phone knows about one document. */
export type LookupState<T> =
  | { kind: 'loading' }
  /** Read; `fromCache` says it came from the phone's own copy (no signal, or not confirmed yet). */
  | { kind: 'found'; row: T; fromCache: boolean }
  /** The server says there is no such document. */
  | { kind: 'missing' }
  /** No signal and the phone never read it. */
  | { kind: 'not_on_phone' }
  /** The rules refuse it: the person may not see it. */
  | { kind: 'not_allowed' }
  | { kind: 'error'; message: string };

export type ScanCodeKind = 'unit' | 'tracking' | 'unknown';

/** A box label (`EB0000012344`), a Prime piece label (`DXB260000013-P1`), a tracking number, or neither. */
export function scanCodeKind(code: string): ScanCodeKind {
  if (barcodeSchema.safeParse(code).success || pieceIdSchema.safeParse(code).success) return 'unit';
  if (trackingNoSchema.safeParse(code).success) return 'tracking';
  return 'unknown';
}

const isDenied = (err: Error): boolean => (err as FirestoreError).code === 'permission-denied';

function watchLookup<T>(
  ref: DocumentReference,
  map: (id: string, data: DocumentData) => T,
  onData: (state: LookupState<T>) => void,
): Unsubscribe {
  return onSnapshot(
    ref,
    (snap) => {
      const fromCache = snap.metadata?.fromCache === true;
      if (snap.exists()) onData({ kind: 'found', row: map(snap.id, snap.data()), fromCache });
      else onData(fromCache ? { kind: 'not_on_phone' } : { kind: 'missing' });
    },
    (err) =>
      onData(isDenied(err) ? { kind: 'not_allowed' } : { kind: 'error', message: err.message }),
  );
}

/** One box by its label, live. */
export function watchOpsBox(
  db: Firestore,
  unitId: string,
  onData: (state: LookupState<BoxRow>) => void,
): Unsubscribe {
  return watchLookup(
    doc(db, 'boxes', unitId),
    (id, data) => toDoc<BoxRow>(id, data, 'barcode'),
    onData,
  );
}

/** The box's shipment, live. A person who may see the box may not see its shipment. */
export function watchOpsShipment(
  db: Firestore,
  trackingNo: string,
  onData: (state: LookupState<ShipmentRow>) => void,
): Unsubscribe {
  return watchLookup(
    doc(db, 'shipments', trackingNo),
    (id, data) => toDoc<ShipmentRow>(id, data, 'trackingNo'),
    onData,
  );
}

/** One scan of the box (`boxes/{id}/events`, 03 §3.6), as the Scan page lists it. */
export interface OpsBoxScan {
  id: string;
  type: string;
  occurredAt: string;
  branchId: string;
  result: string;
  note: string;
}

export const OPS_BOX_SCANS_LIMIT = 20;

/**
 * The box's scans recorded at this branch, newest first. The rules let staff read the scans of
 * their own branches, so the query names the branch (one equality, no composite index); scans at
 * other branches are not listed. At most 20.
 */
export function watchOpsBoxScans(
  db: Firestore,
  args: { unitId: string; branchId: string },
  onData: (rows: OpsBoxScan[]) => void,
  onError: (err: Error) => void,
): Unsubscribe {
  const q = query(
    collection(db, 'boxes', args.unitId, 'events'),
    where('branchId', '==', args.branchId),
    limit(OPS_BOX_SCANS_LIMIT),
  );
  return onSnapshot(
    q,
    (snap) =>
      onData(
        snap.docs
          .map((d) => {
            const data = toDoc<Record<string, unknown>>(d.id, d.data());
            const text = (k: string): string => (typeof data[k] === 'string' ? data[k] : '');
            return {
              id: d.id,
              type: text('type'),
              occurredAt: text('occurredAt'),
              branchId: text('branchId'),
              result: text('result'),
              note: text('note'),
            };
          })
          .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)),
      ),
    onError,
  );
}

/** True when the shipment's freight is paid in full (`money.status`, D-16). */
export function shipmentPaidInFull(shipment: Pick<ShipmentRow, 'money'>): boolean {
  return shipment.money.status === 'paid' || shipment.money.status === 'overpaid';
}
